import React, { useState, useEffect, useCallback } from 'react';
import {
  Truck, Wrench, AlertTriangle, Search, Filter,
  CheckCircle, Clock, Download, ChevronDown, ChevronUp,
  Activity
} from 'lucide-react';
import { getLaporanKlien, getClientList } from '../api';

const COLORS = {
  normal: '#2dce89',
  perhatian: '#fb6340',
  kritis: '#f5365c',
  primary: '#11cdef',
  dark: '#172b4d',
  gray: '#8898aa',
};

function formatPersen(val) {
  return (val || 0).toFixed(1) + '%';
}

// ─── Stat Card ───
function StatCard({ icon: Icon, label, value, color, sublabel }) {
  return (
    <div className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
      <div className="stat-card-body">
        <div className="stat-card-content">
          <h5 className="stat-card-label">{label}</h5>
          <span className="stat-card-value" style={{ color }}>{value}</span>
          {sublabel && <span className="stat-card-sublabel">{sublabel}</span>}
        </div>
        <div className="stat-card-icon-col">
          <div className="stat-icon-shape" style={{ background: color + '18', color }}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Badge Status ───
function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  let bg = '#e9ecef', color = '#525f7f';
  if (s === 'normal') { bg = '#d1fae5'; color = '#065f46'; }
  else if (s === 'perlu perhatian') { bg = '#fef3c7'; color = '#92400e'; }
  else if (s === 'kritis') { bg = '#fee2e2'; color = '#991b1b'; }
  else if (s === 'selesai' || s === 'diverifikasi') { bg = '#d1fae5'; color = '#065f46'; }
  else if (s === 'diproses' || s === 'berlangsung') { bg = '#dbeafe'; color = '#1e40af'; }
  else if (s === 'pengajuan' || s === 'menunggu approval') { bg = '#fef3c7'; color = '#92400e'; }
  else if (s === 'ditolak' || s === 'dibatalkan') { bg = '#fee2e2'; color = '#991b1b'; }
  return (
    <span className="badge" style={{ background: bg, color, fontSize: '0.7rem', padding: '2px 8px' }}>
      {status || '-'}
    </span>
  );
}

// ─── Uptime Bar ───
function UptimeBar({ pct }) {
  const color = pct >= 95 ? COLORS.normal : pct >= 85 ? COLORS.perhatian : COLORS.kritis;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.5s' }} />
      </div>
      <span style={{ fontSize: '0.75rem', fontWeight: 700, color, minWidth: 40, textAlign: 'right' }}>
        {formatPersen(pct)}
      </span>
    </div>
  );
}

// ─── Page Header ───
function PageHeader() {
  return (
    <div className="dash-header">
      <div className="dash-header-bg" />
      <div className="dash-header-content">
        <div className="dash-breadcrumb">
          <span style={{ color: 'rgba(255,255,255,0.6)' }}>Maintenance</span>
          <span className="dash-breadcrumb-sep">/</span>
          <span style={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>Laporan Klien</span>
        </div>
        <h2 className="dash-title">Laporan Statistik Maintenance Armada</h2>
        <p className="dash-subtitle" style={{ color: 'rgba(255,255,255,0.7)' }}>Ringkasan pemeliharaan armada per klien — format laporan bulanan</p>
      </div>
    </div>
  );
}

// =============================================
// MAIN COMPONENT
// =============================================
export default function LaporanKlien() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [periode, setPeriode] = useState(() => {
    const now = new Date();
    return now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0');
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPM, setShowPM] = useState(true);
  const [showCM, setShowCM] = useState(true);
  const [searchVehicle, setSearchVehicle] = useState('');

  // Load client list on mount
  useEffect(() => {
    getClientList()
      .then(res => setClients(res.data || []))
      .catch(() => {});
  }, []);

  // Fetch data
  const fetchData = useCallback(() => {
    setLoading(true);
    setError('');
    const clientParam = selectedClient === 'all' ? '' : selectedClient;
    getLaporanKlien({ client_name: clientParam, periode })
      .then(res => {
        setData(res.data);
      })
      .catch(err => {
        console.error('Failed to load client report', err);
        const msg = err.response?.data?.error || 'Gagal memuat data. Periksa filter dan koneksi server.';
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [selectedClient, periode]);

  // ─── Generate PDF ───
  const generatePDF = () => {
    if (!data) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('landscape', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;

    doc.setFillColor(17, 205, 239);
    doc.rect(0, 0, pageW, 32, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('DATA STATISTIK MAINTENANCE ARMADA', margin, 14);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(`${data.client_name} — Periode: ${data.periode}`, margin, 22);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Layanan Synexa', pageW - margin, 14, { align: 'right' });

    const startY = 42;
    doc.setDrawColor(230, 230, 230);
    doc.setFillColor(248, 249, 252);
    doc.roundedRect(margin, startY, pageW - 2 * margin, 28, 3, 3, 'FD');

    const statW = (pageW - 2 * margin - 20) / 5;
    const statColors = [[17, 205, 239], [45, 206, 137], [245, 54, 92], [17, 205, 239], [251, 99, 64]];
    const stats = [
      { label: 'Total Armada', value: data.total_armada },
      { label: 'Normal', value: `${data.total_normal} unit` },
      { label: 'Gangguan', value: `${data.total_gangguan} unit` },
      { label: 'Total PM', value: data.total_pm },
      { label: 'Total CM', value: data.total_cm },
    ];

    stats.forEach((s, i) => {
      const [r, g, b] = statColors[i];
      doc.setFillColor(r, g, b);
      doc.rect(margin + 5 + i * (statW + 5), startY + 5, 3, 18, 'F');
      doc.setTextColor(136, 152, 170);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text(s.label, margin + 12 + i * (statW + 5), startY + 10);
      doc.setTextColor(23, 43, 77);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(String(s.value), margin + 12 + i * (statW + 5), startY + 22);
    });

    let yPos = startY + 40;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(23, 43, 77);
    doc.text('A. STATUS ARMADA PER UNIT', margin, yPos);
    yPos += 6;

    autoTable(doc, {
      startY: yPos,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7, cellPadding: 2, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
      headStyles: { fillColor: [94, 114, 228], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
      head: [['No', 'No Body', 'No Polisi', 'AC/Non AC', 'PM', 'CM', 'Uptime', 'Status', 'Rekomendasi']],
      body: (data.vehicles || []).slice(0, 50).map((v, i) => [
        (i + 1).toString(), v.body_no || '-', v.plate_no || '-',
        v.jenis_ac || '-', v.pm_count.toString(), v.cm_count.toString(),
        formatPersen(v.uptime_percent), v.status || '-', v.rekomendasi || '-',
      ]),
      columnStyles: { 0: { halign: 'center', cellWidth: 10 }, 6: { halign: 'center', cellWidth: 16 }, 7: { halign: 'center', cellWidth: 20 }, 8: { cellWidth: 30 } },
    });

    if (data.pm_list?.length > 0) {
      yPos = doc.lastAutoTable.finalY + 12;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(23, 43, 77);
      doc.text('B. PREVENTIVE MAINTENANCE (PM)', margin, yPos);
      yPos += 6;
      autoTable(doc, {
        startY: yPos,
        margin: { left: margin, right: margin },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [17, 205, 239], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
        head: [['No', 'ID PM', 'Tanggal', 'No Body', 'No Polisi', 'Rute', 'Teknisi', 'Catatan']],
        body: data.pm_list.map((p, i) => [
          (i + 1).toString(), p.pm_id || '-', p.tanggal || '-',
          p.no_body || '-', p.no_polisi || '-', p.rute || '-',
          p.teknisi || '-', (p.catatan || '').substring(0, 60),
        ]),
        columnStyles: { 0: { halign: 'center', cellWidth: 10 } },
      });
    }

    if (data.cm_list?.length > 0) {
      yPos = doc.lastAutoTable.finalY + 12;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(23, 43, 77);
      doc.text('C. CORRECTIVE MAINTENANCE (CM)', margin, yPos);
      yPos += 6;
      autoTable(doc, {
        startY: yPos,
        margin: { left: margin, right: margin },
        styles: { fontSize: 7, cellPadding: 2, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [251, 99, 64], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7, halign: 'center' },
        head: [['No', 'ID CM', 'Tanggal', 'No Body', 'No Polisi', 'Gangguan', 'Sparepart', 'Teknisi', 'Durasi', 'Status']],
        body: data.cm_list.map((c, i) => [
          (i + 1).toString(), c.cm_id || '-', c.tanggal || '-',
          c.no_body || '-', c.no_polisi || '-',
          (c.jenis_pekerjaan || '').substring(0, 25),
          (c.sparepart_komponen || '').substring(0, 25),
          c.nama_teknisi || '-', c.durasi_pengerjaan || '-', c.status || '-',
        ]),
        columnStyles: { 0: { halign: 'center', cellWidth: 10 } },
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const h = doc.internal.pageSize.getHeight();
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, h - 18, pageW - margin, h - 18);
      doc.setTextColor(136, 152, 170);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      const now = new Date();
      const dateStr = now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`Dicetak pada: ${dateStr} | Laporan: ${data.client_name} - ${data.periode}`, margin, h - 10);
      doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, h - 10, { align: 'right' });
    }

    const filename = `Statistik_Maintenance_${data.client_name.replace(/\s+/g, '_')}_${data.periode}.pdf`;
    doc.save(filename);
  };

  // ── Filtered vehicles ──
  const filteredVehicles = (data?.vehicles || []).filter(v => {
    if (!searchVehicle) return true;
    const q = searchVehicle.toLowerCase();
    return (v.body_no || '').toLowerCase().includes(q) ||
           (v.plate_no || '').toLowerCase().includes(q) ||
           (v.route_code || '').toLowerCase().includes(q);
  });

  // ── Render ──
  return (
    <div className="dash-wrapper">
      <PageHeader />

      <div className="dash-content">
        {/* ═══ FILTERS ═══ */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24 }}>
          <div style={{ minWidth: 220 }}>
            <label style={{ fontSize: '0.75rem', marginBottom: 4, color: '#32325d', display: 'block', fontWeight: 600 }}>
              Pilih Klien <span style={{ color: '#f5365c' }}>*</span>
            </label>
            <select
              value={selectedClient}
              onChange={e => setSelectedClient(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #adb5bd', borderRadius: 8, fontSize: '0.85rem', background: '#fff', color: '#32325d' }}
            >
              <option value="">— Pilih Klien —</option>
              <option value="all">📋 Semua Klien</option>
              {clients.map((c, i) => (
                <option key={i} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: 160 }}>
            <label style={{ fontSize: '0.75rem', marginBottom: 4, color: '#32325d', display: 'block', fontWeight: 600 }}>
              Periode (Bulan)
            </label>
            <input
              type="month"
              value={periode}
              onChange={e => setPeriode(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #adb5bd', borderRadius: 8, fontSize: '0.85rem', color: '#32325d' }}
            />
          </div>

          <button
            className="btn btn-primary"
            onClick={fetchData}
            disabled={!selectedClient || loading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38 }}
          >
            <Filter size={16} />
            {loading ? 'Memuat...' : 'Tampilkan Laporan'}
          </button>

          {data && (
            <>
              <button
                className="btn btn-primary"
                onClick={generatePDF}
                style={{ display: 'flex', alignItems: 'center', gap: 6, height: 38, background: 'linear-gradient(135deg, #f5365c, #fb6340)', border: 'none' }}
              >
                <Download size={16} />
                Export PDF
              </button>

              <div className="search-box" style={{ marginLeft: 'auto' }}>
                <Search size={16} />
                <input
                  placeholder="Cari No Body/Polisi..."
                  value={searchVehicle}
                  onChange={e => setSearchVehicle(e.target.value)}
                  style={{ border: 'none', outline: 'none', background: 'none', fontSize: '0.9rem', width: '100%', color: '#32325d' }}
                />
              </div>
            </>
          )}
        </div>

        {/* ═══ ERROR ═══ */}
        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} />
            <span style={{ fontWeight: 500 }}>{error}</span>
          </div>
        )}

        {/* ═══ LOADING ═══ */}
        {loading && !data && (
          <div className="dash-loading" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 0 2rem 0 rgba(136, 152, 170, 0.15)', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, color: '#8898aa' }}>
            <div className="spinner-lg" />
            <p>Memuat data laporan...</p>
          </div>
        )}

        {/* ═══ INITIAL STATE ═══ */}
        {!selectedClient && !data && !loading && (
          <div style={{ textAlign: 'center', padding: 60, color: '#8898aa', background: '#fff', borderRadius: 12, boxShadow: '0 0 2rem 0 rgba(136, 152, 170, 0.15)' }}>
            <Truck size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
            <h3 style={{ color: '#32325d', marginBottom: 8, fontSize: '1.2rem' }}>Laporan Statistik Maintenance Armada</h3>
            <p style={{ color: '#8898aa' }}>Pilih klien dan periode untuk menampilkan laporan statistik maintenance armada.</p>
            <p style={{ fontSize: '0.8rem', marginTop: 8, color: '#8898aa' }}>
              Laporan ini menampilkan ringkasan kondisi armada, status per unit, 
              detail Preventive Maintenance (PM), dan Corrective Maintenance (CM).
            </p>
          </div>
        )}

        {/* ═══ DATA ═══ */}
        {data && (
          <>
            {/* ── Summary Stats ── */}
            <div className="dash-stats-row" style={{ gridTemplateColumns: "repeat(5, 1fr)", marginBottom: 20 }}>
              <div className="dash-stat-col">
                <StatCard icon={Truck} label="Total Armada" value={data.total_armada} color={COLORS.primary}
                  sublabel={data.client_name} />
              </div>
              <div className="dash-stat-col">
                <StatCard icon={CheckCircle} label="Normal" value={`${data.total_normal} unit`} color={COLORS.normal}
                  sublabel={data.total_armada > 0 ? `${Math.round(data.total_normal / data.total_armada * 100)}% dari total` : '0%'} />
              </div>
              <div className="dash-stat-col">
                <StatCard icon={AlertTriangle} label="Gangguan" value={`${data.total_gangguan} unit`} color={COLORS.perhatian}
                  sublabel={data.total_armada > 0 ? `${Math.round(data.total_gangguan / data.total_armada * 100)}% dari total` : '0%'} />
              </div>
              <div className="dash-stat-col">
                <StatCard icon={Activity} label="Total PM" value={data.total_pm} color={COLORS.primary}
                  sublabel={data.total_pm > 0 ? `${(data.total_pm / data.total_armada).toFixed(1)} per unit` : 'Belum ada'} />
              </div>
              <div className="dash-stat-col">
                <StatCard icon={Wrench} label="Total CM" value={data.total_cm} color={COLORS.perhatian}
                  sublabel={data.total_cm > 0 ? `${(data.total_cm / data.total_armada).toFixed(1)} per unit` : 'Belum ada'} />
              </div>
            </div>

            {/* Rata-rata Uptime */}
            <div style={{
              background: 'linear-gradient(135deg, #0a3845, #073845)',
              borderRadius: 12, padding: '16px 24px', marginBottom: 24,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: '0.75rem', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#fff' }}>
                  Rata-rata Uptime Armada
                </div>
                <div style={{ fontSize: '1.8rem', fontWeight: 800, marginTop: 4, color: '#fff' }}>
                  {formatPersen(data.avg_uptime)}
                </div>
                <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: 2, color: '#fff' }}>
                  Periode: {data.periode}
                </div>
              </div>
              <div style={{
                width: 80, height: 80, borderRadius: '50%',
                background: `conic-gradient(#2dce89 0deg ${data.avg_uptime * 3.6}deg, rgba(255,255,255,0.15) ${data.avg_uptime * 3.6}deg 360deg)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div style={{
                  width: 60, height: 60, borderRadius: '50%',
                  background: '#0a3845', display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '0.65rem', opacity: 0.7, color: '#fff' }}>UP</span>
                  <span style={{ fontSize: '1rem', fontWeight: 800, lineHeight: 1, color: '#fff' }}>
                    {data.avg_uptime ? Math.round(data.avg_uptime) : 0}%
                  </span>
                </div>
              </div>
            </div>

            {/* ── Vehicle Status Table ── */}
            <div className="page-header-row" style={{ marginBottom: 12 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#32325d' }}>
                A. Status Armada Per Unit
                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#8898aa', marginLeft: 8 }}>
                  ({filteredVehicles.length} unit)
                </span>
              </h3>
            </div>

            <div className="table-wrapper" style={{ marginBottom: 24 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>No Body</th>
                    <th>No Polisi</th>
                    <th>AC/Non AC</th>
                    <th>Rute</th>
                    <th>PM</th>
                    <th>CM</th>
                    <th>Uptime</th>
                    <th>Status</th>
                    <th>Rekomendasi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map((v, i) => (
                    <tr key={v.id}>
                      <td style={{ textAlign: 'center' }}>{i + 1}</td>
                      <td><strong style={{ color: '#32325d' }}>{v.body_no || '-'}</strong></td>
                      <td style={{ color: '#525f7f' }}>{v.plate_no || '-'}</td>
                      <td>
                        <span style={{
                          padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600,
                          background: v.jenis_ac === 'AC' ? '#dbeafe' : '#f3f4f6',
                          color: v.jenis_ac === 'AC' ? '#1e40af' : '#6b7280',
                        }}>
                          {v.jenis_ac || 'Non AC'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: '#525f7f' }}>{v.route_code || '-'}</td>
                      <td style={{ textAlign: 'center', color: '#525f7f' }}>{v.pm_count}</td>
                      <td style={{ textAlign: 'center', color: '#525f7f' }}>{v.cm_count}</td>
                      <td><UptimeBar pct={v.uptime_percent} /></td>
                      <td><StatusBadge status={v.status} /></td>
                      <td style={{ fontSize: '0.75rem', color: '#8898aa' }}>{v.rekomendasi || '-'}</td>
                    </tr>
                  ))}
                  {filteredVehicles.length === 0 && (
                    <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#8898aa' }}>Tidak ada data kendaraan.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── PM Section ── */}
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={() => setShowPM(!showPM)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '1rem', fontWeight: 700, color: '#32325d', padding: 0,
                }}
              >
                {showPM ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                B. Preventive Maintenance (PM)
                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#8898aa', marginLeft: 4 }}>
                  ({data.pm_list?.length || 0} kegiatan)
                </span>
              </button>
            </div>

            {showPM && (
              <div className="table-wrapper" style={{ marginBottom: 24 }}>
                <table className="data-table">
                  <thead>
                    <tr style={{ background: 'linear-gradient(90deg, rgba(17,205,239,0.1), rgba(17,205,239,0.03))' }}>
                      <th>No</th>
                      <th>ID PM</th>
                      <th>Tanggal</th>
                      <th>No Body</th>
                      <th>No Polisi</th>
                      <th>Rute</th>
                      <th>Teknisi</th>
                      <th>Jenis Pemeriksaan</th>
                      <th>Catatan</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.pm_list || []).map((p, i) => (
                      <tr key={p.id}>
                        <td style={{ textAlign: 'center', color: '#525f7f' }}>{i + 1}</td>
                        <td><code style={{ fontSize: '0.7rem', background: '#f3f4f6', padding: '2px 4px', borderRadius: 4, color: '#525f7f' }}>{p.pm_id || '-'}</code></td>
                        <td style={{ color: '#525f7f' }}>{p.tanggal || '-'}</td>
                        <td><strong style={{ color: '#32325d' }}>{p.no_body || '-'}</strong></td>
                        <td style={{ color: '#525f7f' }}>{p.no_polisi || '-'}</td>
                        <td style={{ fontSize: '0.8rem', color: '#525f7f' }}>{p.rute || '-'}</td>
                        <td style={{ fontSize: '0.8rem', color: '#525f7f' }}>{p.teknisi || '-'}</td>
                        <td style={{ fontSize: '0.75rem', maxWidth: 150, color: '#525f7f' }}>{p.jenis_pemeriksaan || '-'}</td>
                        <td style={{ fontSize: '0.75rem', maxWidth: 200, color: '#8898aa' }}>
                          {p.catatan || '-'}
                        </td>
                        <td><StatusBadge status={p.status} /></td>
                      </tr>
                    ))}
                    {(!data.pm_list || data.pm_list.length === 0) && (
                      <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#8898aa' }}>Belum ada data PM untuk periode ini.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── CM Section ── */}
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={() => setShowCM(!showCM)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '1rem', fontWeight: 700, color: '#32325d', padding: 0,
                }}
              >
                {showCM ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                C. Corrective Maintenance (CM)
                <span style={{ fontSize: '0.75rem', fontWeight: 400, color: '#8898aa', marginLeft: 4 }}>
                  ({data.cm_list?.length || 0} kegiatan)
                </span>
              </button>
            </div>

            {showCM && (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr style={{ background: 'linear-gradient(90deg, rgba(251,99,64,0.1), rgba(251,99,64,0.03))' }}>
                      <th>No</th>
                      <th>ID CM</th>
                      <th>Tanggal</th>
                      <th>No Body</th>
                      <th>No Polisi</th>
                      <th>Jenis Gangguan</th>
                      <th>Sparepart</th>
                      <th>Teknisi</th>
                      <th>Durasi</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.cm_list || []).map((c, i) => (
                      <tr key={c.id}>
                        <td style={{ textAlign: 'center', color: '#525f7f' }}>{i + 1}</td>
                        <td><code style={{ fontSize: '0.7rem', background: '#f3f4f6', padding: '2px 4px', borderRadius: 4, color: '#525f7f' }}>{c.cm_id || '-'}</code></td>
                        <td style={{ color: '#525f7f' }}>{c.tanggal || '-'}</td>
                        <td><strong style={{ color: '#32325d' }}>{c.no_body || '-'}</strong></td>
                        <td style={{ color: '#525f7f' }}>{c.no_polisi || '-'}</td>
                        <td style={{ fontSize: '0.75rem', maxWidth: 180, color: '#525f7f' }}>
                          <div><strong style={{ color: '#32325d' }}>{c.jenis_pekerjaan || '-'}</strong></div>
                          {c.detail_pekerjaan && (
                            <div style={{ color: '#8898aa', fontSize: '0.7rem', marginTop: 2 }}>
                              {c.detail_pekerjaan.substring(0, 50)}
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: '0.75rem', maxWidth: 150, color: '#8898aa' }}>
                          {c.sparepart_komponen || '-'}
                        </td>
                        <td style={{ fontSize: '0.8rem', color: '#525f7f' }}>{c.nama_teknisi || '-'}</td>
                        <td>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            fontSize: '0.75rem', fontWeight: 600, color: '#11cdef',
                          }}>
                            <Clock size={12} />
                            {c.durasi_pengerjaan || '-'}
                          </span>
                        </td>
                        <td><StatusBadge status={c.status} /></td>
                      </tr>
                    ))}
                    {(!data.cm_list || data.cm_list.length === 0) && (
                      <tr><td colSpan={10} style={{ textAlign: 'center', padding: 40, color: '#8898aa' }}>Belum ada data CM untuk periode ini.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
